import React, { useCallback, useState, useEffect, useRef } from 'react';
import ReactFlow, {
  addEdge,
  Node,
  Edge,
  Connection,
  useEdgesState,
  useNodesState,
  MarkerType,
  updateEdge,
} from 'react-flow-renderer';
import {
  init_SignInComponent,
  init_DefaultComponent,
  init_Flowchart,
  init_AADReadComponent,
  init_AADUpdateComponent,
  init_AADCreateComponent,
  init_ConditionalComponent,
  init_UsernamePasswordValidation,
  init_UserInputPageComponent,
  init_ValidateMagicLink,
  init_TokenIssuer,
  init_RESTAPIComponent,
  init_MFAAuth,
  init_WebAuthnRegistration,
  init_WebAuthnAuthentication,
  init_WebAuthnCredentialsExist,
  init_WebAuthnAvailability,
  init_ConditionalAccessEvaluation,
  init_ConditionalAccessRemediation,
  init_ErrorPage as init_ErrorPage,
  init_AppInsightsComponent,
  init_UserInfoIssuer,
  init_AuthorizeToken,
} from './init_components';
import {
  getFlowchartNodesEdges,
  getNodeData,
  getFlowcharts,
  getCurrentFlowchartId,
  getCurrentTabId,
  setCurrentTabId as setCurrentTabIdName,
  getCurrentOpenNodeId,
  setCurrentOpenNodeId,
  deleteNodeById,
  deleteEdgeById,
  setCurrentOpenNodeData,
  setCurrentFlowchartName,
  setFlowcharts,
  setNodeData,
  setSubFC,
  fetchTemplates,
  getJsonEditor,
  setValidationResults,
  getFeedbackComp,
  setFeedbackComp,
  setJSONEditor,
  getTemplatizationFields,
  setTemplatizationField,
  deleteTemplatedComponent,
  getTemplatedComponents,
  setTemplatedComponent,
  getTemplatizationEnvironments,
  setJSONSessionFlag,
  getJSONSession,
  deleteJSONSessionFlagById,
  setJSONSSOSession,
  initialState
} from '../../store';
import { useSelector, useDispatch } from 'react-redux';
import { isCirclic } from '../../utils/utils';
import jsonComponentsConfig from '../../constants/jsonComponents';
import AAD_ATTRIBUTES from '../../constants/aadAttributes';
import DEFAULT_CLAIMS from '../../constants/defaultClaims';
import { Claim, AADAttribute } from '../../xmlParsing/interfaces';
import { init_Claim, init_AADAttribute } from '../../xmlParsing/initInterfaces';
import { OptionProps } from '../../view-models';
import { testValidateJSON, getInitComponent, getActiveDefaultEnvironments, extractComponentLabel, getSubflowDisplayLabel, getDefaultDeploymentEnv } from './utils';
import { generateTemplate } from './templateGenerator/templateGeneratorUtil';
import editorConstants from '../../constants/jsonConstants';
import jexgSettings from '../../constants/jsonXMLsettings';
import { cloneDeep, isEqual } from 'lodash';
import jsonConstants from '../../constants/jsonConstants';
import defaultEnvs from '../../constants/defaultEnvs';

const useJSONEditorHook = () => {
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [anchorPoint, setAnchorPoint] = useState({ x: 0, y: 0 });
  const [show, setShow] = useState<string>('');
  const [promptEmail, setPromptEmail] = useState<boolean>(false);
  const [initialEmailPrompted, setInitialEmailPrompted] = useState<boolean>(false);
  const [showTOS, setShowTOS] = useState<boolean>(!localStorage.getItem('userId'));
  const [selectedNE, setSelectedNE] = useState<any>({});
  const [rightSection, setRightSection] = useState<'flowchart' | 'claims' | 'aad attributes' | 'app settings'>('flowchart');
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [flowchartsList, setFlowchartsList] = useState<OptionProps[]>([]);
  const [subFlowList, setSubFlowList] = useState<OptionProps[]>([]);
  const [zoom, setZoom] = useState<number>(1);
  const [newClaim, setNewClaim] = useState<Claim>(init_Claim());
  const [newAADAttribute, setNewAADAttribute] = useState<AADAttribute>(init_AADAttribute());
  const [deployState, setDeployState] = useState<string>('');
  const [enableJavascript, setEnableJavascript] = useState<boolean>(true);
  const [journeyInsightsSettings, setJourneyInsightsSettings] = useState({
    enableJourneyInsights: true
  })
  const [templatizedDownload, setTemplatizedDownload] = useState<boolean>(true);
  const [envsForAppSettings, setEnvsForAppSettings] = useState<any>(
    Object.keys(jsonConstants.defaultEnvMap).reduce((dEnvs: { [index: string]: any }, dEnvKey: string) => {
      if (jsonConstants[dEnvKey as keyof typeof jsonConstants]) {
        let dEnvId = jsonConstants.defaultEnvMap[dEnvKey as keyof typeof jsonConstants.defaultEnvMap];
        dEnvs[dEnvId] = { id: dEnvId, name: defaultEnvs[dEnvId as keyof typeof defaultEnvs].name, included: true };
      }
      return dEnvs;
    }, {})
  );
  const reactFlowWrap = useRef<HTMLDivElement>(null);
  const edgeUpdateSuccessful = useRef(true);
  const dispatch = useDispatch();
  const allComponentsData = useSelector(getNodeData);
  const currentFlowchartId = useSelector(getCurrentFlowchartId);
  const allFlowcharts = useSelector(getFlowcharts);
  const flowchartNodesEdges = useSelector(getFlowchartNodesEdges);
  const currentOpenNodeId = useSelector(getCurrentOpenNodeId);
  const currentTabId = useSelector(getCurrentTabId);
  const editorState = useSelector(getJsonEditor);
  const showFeedbackComp = useSelector(getFeedbackComp);
  const templateFields = useSelector(getTemplatizationFields);
  const allEnvironments = useSelector(getTemplatizationEnvironments);
  const templatedComponents = useSelector(getTemplatedComponents);
  const ssoSession = useSelector(getJSONSession);

  const [deploymentEnv, setDeploymentEnv] = useState<string>(getDefaultDeploymentEnv(allEnvironments));
  const [downloadEnv, setDownloadEnv] = useState<string>(getDefaultDeploymentEnv(allEnvironments));

  useEffect(() => {
    dispatch(fetchTemplates());
  }, []);
  useEffect(() => {
    if (Object.keys(allFlowcharts).length > 0) {
      // fetch all userjourneys
      const temp = Object.keys(allFlowcharts)
        .filter((val: string) => allFlowcharts[val]['flowchartType'] === 'UserJourney')
        .reduce((acc: OptionProps[], curVal: string) => {
          return acc.concat({ label: allFlowcharts[curVal]['flowchartName'], value: curVal });
        }, []);
      setFlowchartsList(temp);
    } else {
      // create a new flowchart ID
      setShow('startWizard');
    }
  }, [allFlowcharts]);

  useEffect(() => {
    setEnvsForAppSettings(Object.keys(allEnvironments).reduce((envs: { [index: string]: any }, envId: string) => {
      envs[envId] = {
        id: envId,
        label: allEnvironments[envId].name,
        included: Object.keys(envsForAppSettings).includes(envId) ? envsForAppSettings[envId].included : true
      };
      return envs;
    }, {}));
    if (!Object.keys(allEnvironments).includes(downloadEnv)) {
      setDownloadEnv(jsonConstants.enableGritDevEnvDefaultEnv ?
        jsonConstants.defaultEnvMap['enableGritDevEnvDefaultEnv'] :
        (jsonConstants.enableGritSandboxDefaultEnv ?
          jsonConstants.defaultEnvMap['enableGritSandboxDefaultEnv'] : (
            Object.keys(allEnvironments).length > 0 ?
              Object.keys(allEnvironments)[0] :
              ''
          )
        )
      );
      if (Object.keys(allEnvironments).length == 0) {
        setTemplatizedDownload(true);
      }
    }
  }, [allEnvironments]);

  const startWizardCancel = () => {
    setShow('createNewFC');
  };

  useEffect(() => {
    if (currentFlowchartId && allFlowcharts.hasOwnProperty(currentFlowchartId)) {
      const temp = allFlowcharts[currentFlowchartId]['components'].reduce((acc: OptionProps[], curCom: string) => {
        let next = acc;
        if (allComponentsData.hasOwnProperty(curCom)) {
          ['formSubmitAction', 'userInfoEndpoint', 'emailVerificationSendAction'].forEach((subflowType) => {
            if (allComponentsData[curCom].hasOwnProperty(subflowType)) {
              const subflowId = allComponentsData[curCom][subflowType]['id'];
              if (subflowId && subflowId !== '' && allFlowcharts.hasOwnProperty(subflowId)) {
                next = next.concat({
                  label: allComponentsData[curCom][subflowType]['displayName'],
                  value: subflowId,
                });
              }
            }
          });
        }
        return next;
      }, []);
      setSubFlowList(temp);
      if (!initialEmailPrompted) {
        setInitialEmailPrompted(true);
        window.setTimeout(() => {
          setPromptEmail(editorConstants.emailPromptEnabled);
        }, 45000);
      }
    }
  }, [currentFlowchartId, allFlowcharts]);

  const closeFeedback = () => {
    dispatch(setFeedbackComp(false));
  };

  const addNewFlowchart = (type: string, name: string) => {
    const newFlowchart: any = init_Flowchart(type, name);
    dispatch(
      setFlowcharts({
        flowchart: newFlowchart,
        currentFlowchartId: newFlowchart['id'],
        claims: DEFAULT_CLAIMS,
        aadAttributes: AAD_ATTRIBUTES,
      }),
    );
    setNodes([]);
    setEdges([]);
  }

  const onCloseCreateNewPopup = (
    action: 'template' | 'cancel' | 'upload' | 'close' | 'createNew' | 'templateBuilder',
    param: any,
  ) => {
    if (action === 'createNew' && param) {
      // When new journey is created
      if (show === 'createNewFC') {
        // create new user journey
        const newEditorState: any = cloneDeep(initialState);
        const newFlowchart: any = init_Flowchart('UserJourney', param);
        newEditorState.currentFlowchartId = newFlowchart['id'];
        newEditorState.currentTabId = newFlowchart['id'];
        newEditorState.claims = { ...newEditorState.claims, ...DEFAULT_CLAIMS };
        newEditorState.aadAttributes = { ...newEditorState.aadAttributes, ...AAD_ATTRIBUTES };
        newEditorState.flowcharts[newFlowchart['id']] = newFlowchart;
        dispatch(
          setJSONEditor({
            ...newEditorState
          }),
        );
      }
      setNodes([]);
      setEdges([]);
    } else if (action === 'cancel') {
      // When Cancel is clicked
      if (show === 'createNewFC') {
        const firstFC = Object.keys(allFlowcharts)[0];
        dispatch(setCurrentFlowchartName(allFlowcharts[firstFC]['id']));
      }
    } else {
      if (action === 'templateBuilder') {
        const generatedTemplate = generateTemplate(param)
        dispatch(setJSONEditor(generatedTemplate));
        if (jexgSettings.instrumentPolicyHTMLWithLR) {
          setEnableJavascript(true);
        }
      }
      setTimeout(() => {
        if (reactFlowInstance && nodes.length) {
          // @ts-ignore
          reactFlowInstance.fitView();
        }
      }, 100);
    }

    setShow('');
  };

  useEffect(() => {
    if (nodes.length > 0) {
      const components: string[] = nodes.reduce(
        (acc: string[], curComponent: any) => acc.concat(curComponent['id']),
        [],
      );
      dispatch(
        setFlowcharts({
          flowchart: {
            ...allFlowcharts[currentTabId],
            nodes: nodes,
            edges: edges,
            components: components,
          },
        }),
      );
    }
  }, [nodes.length, edges.length]);

  useEffect(() => {
    setNodes(flowchartNodesEdges['nodes']);
    setEdges(flowchartNodesEdges['edges']);
  }, [flowchartNodesEdges]);

  const onInit = (rf: any) => {
    setReactFlowInstance(rf);
  };

  const setCurrentFlowchart = (fcName: string) => {
    dispatch(setCurrentFlowchartName(fcName));
  };

  const setCurrentTabId = (fcName: string) => {
    dispatch(setCurrentTabIdName(fcName));
  };

  const onNodeContextMenu = useCallback(
    (event: React.MouseEvent, node: Node) => {
      event.preventDefault();
      setSelectedNE(node);
      setAnchorPoint({ x: event.pageX, y: event.pageY });
      setShow('nodeMenu');
    },
    [setAnchorPoint],
  );
  const onNodeDragStop = (event: React.MouseEvent, node: Node) => {
    event.preventDefault();
    const components: string[] = nodes.reduce((acc: string[], curComponent: any) => acc.concat(curComponent['id']), []);
    dispatch(
      setFlowcharts({
        flowchart: {
          ...allFlowcharts[currentTabId],
          nodes: nodes,
          edges: edges,
          components: components,
        },
      }),
    );
  };

  const onEdgeContextMenu = useCallback(
    (event: React.MouseEvent, edge: Edge) => {
      event.preventDefault();
      setSelectedNE(edge);
      setAnchorPoint({ x: event.pageX, y: event.pageY });
      setShow('edgeMenu');
    },
    [setAnchorPoint],
  );

  // const handleContextMenu = useCallback(
  //   (event) => {
  //     event.preventDefault();
  //     setAnchorPoint({ x: event.pageX, y: event.pageY });
  //     setShow('menu');
  //   },
  //   [setAnchorPoint, setShow]
  // );

  const onPaneContextMenu = (event: React.MouseEvent) => {
    setShow('');
    event.preventDefault();
    setAnchorPoint({ x: event.pageX, y: event.pageY });
  };
  const onNodeClick = (event: React.MouseEvent, node: Node) => {
    setShow('');
  };

  const handleConnection = (connection: Connection, allowLinkConnections: boolean, componentsData: any) => {
    /*object contains tree structure
      ex: {
        'precondition-0-1' : [targetIds...],
        '1':[targetIds...],
        ...
      }
      */
    const obj: any = {};
    edges.forEach((edge) => {
      obj[edge.source] = obj[edge.source] || [];
      obj[edge.source].push(edge.target);
    });
    /*
      add new connection if tree doesn't contain circle structure
      */
    // @ts-ignore
    if (!isCirclic(obj, connection.source, connection.target) && connection.target !== connection.source) {
      if (allowLinkConnections || !connection.target || !componentsData[connection.target].linkId) {
        return setEdges((eds) =>
          addEdge(
            {
              ...connection,
              markerEnd: { type: MarkerType.ArrowClosed },
              style: { stroke: connection.sourceHandle === 'right' ? 'red' : 'green' },
            },
            eds,
          ),
        );
      }
    } else {
      return null;
    }
  };

  const onConnect = useCallback(
    (connection: Connection) => {
      return handleConnection(connection, false, allComponentsData);
    },
    [setEdges, edges, allComponentsData],
  );

  const onAutoConnect = useCallback(
    (connection: Connection) => {
      return handleConnection(connection, true, allComponentsData);
    },
    [setEdges, edges, allComponentsData],
  );

  const onNodeDoubleClick = (event: React.MouseEvent, node: Node) => {
    if (allComponentsData[node.id].linkType !== 'localAccountSignIn') {
      setShow('popup');
      dispatch(setCurrentOpenNodeId(node.id));
    }
  }

  const onDrop = useCallback(
    (event) => {
      setShow('');
      event.preventDefault();
      // @ts-ignore
      const reactFlowBounds = reactFlowWrap.current.getBoundingClientRect();
      const type = event.dataTransfer.getData('application/reactflow');

      // check if the dropped element is valid
      if (typeof type === 'undefined' || !type) {
        return;
      }
      // @ts-ignore
      const position = reactFlowInstance.project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });
      /*add new node when we drop process or condition boxes */
      // @ts-ignore
      const componentConfigData = jsonComponentsConfig[type];
      let initComponent: any = getInitComponent(type);
      initComponent.flowchart = currentTabId;
      if (['formSubmitAction', 'emailVerificationSendAction', 'userInfoEndpoint'].includes(allFlowcharts[currentTabId].flowchartType)) {
        initComponent.subflowParent = allFlowcharts[currentTabId].parentComponentId;
        // UsernamePasswordValidation has special handling if it is within the submit action of a signin node
        if ((type === 'usernamePasswordValidation') && (allComponentsData[initComponent.subflowParent].componentType === 'signIn')) {
          initComponent.username = allComponentsData[initComponent.subflowParent].username;
          initComponent.usernameType = allComponentsData[initComponent.subflowParent].usernameType;
        }
      }
      dispatch(setNodeData({ [initComponent['id']]: initComponent }));
      setNodes((nds: Node[]) => {
        const newNode: any = {
          id: initComponent['id'],
          type: componentConfigData['nodeType'],
          componentType: type,
          position,
          data: { label: `${componentConfigData['label']}` },
        };
        return nds.concat(newNode);
      });
    },
    [reactFlowInstance, nodes],
  );

  useEffect(() => {
    dispatch(setValidationResults(testValidateJSON(editorState, allFlowcharts)));
  }, [allFlowcharts]);

  useEffect(() => {
    // If the currently-selected deployment or download envs got deleted, fall back to the first available default.
    if (!Object.keys(allEnvironments).includes(deploymentEnv)) {
      setDeploymentEnv(getDefaultDeploymentEnv(allEnvironments));
    }
    if (!Object.keys(allEnvironments).includes(downloadEnv)) {
      setDownloadEnv(getDefaultDeploymentEnv(allEnvironments));
    }
    dispatch(setValidationResults(testValidateJSON(editorState, allFlowcharts)));
  }, [allEnvironments])

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onEdgeUpdateStart = useCallback(() => {
    // console.log('EUS');
    edgeUpdateSuccessful.current = false;
  }, []);

  const onEdgeUpdate = useCallback((oldEdge, newConnection) => {
    edgeUpdateSuccessful.current = true;
    // console.log('EUU');
    if (!allComponentsData[newConnection.target]?.linkId) {
      setEdges((els) => updateEdge(oldEdge, newConnection, els));
    }
  }, []);

  const onEdgeUpdateEnd = useCallback((_, edge) => {
    // console.log('EUE');
    if ((!edgeUpdateSuccessful.current) && (!(allComponentsData[edge.target]?.linkId))) {
      setEdges((eds) => eds.filter((e) => e.id !== edge.id));
    }

    edgeUpdateSuccessful.current = true;
  }, []);

  const onCloseComponent = (data: any, templatizations: any, addSubFlow: boolean, subFlowType?: string) => {
    if (data) {
      if (data['componentType'] === 'signIn') {
        const closeActions = data['closeActions'];
        delete data['closeActions'];
        closeActions['addNodes'].forEach((node: any, ind: number) => {
          setNodes((nds: Node[]) => {
            return nds.concat({
              ...node,
              // @ts-ignore
              position: reactFlowInstance.project({
                x: closeActions['posArray'][ind]['x'],
                y: closeActions['posArray'][ind]['y'],
              }),
            });
          });
          onAutoConnect({
            source: data['id'],
            sourceHandle: null,
            target: node['id'],
            targetHandle: null,
          });
        });
        closeActions['delNodes'].forEach((id: string) => {
          if (Object.keys(templatedComponents).includes(id)) {
            templatedComponents[id].settings.forEach((settingsField: string) => {
              dispatch(setTemplatizationField({
                [settingsField]: {
                  ...templateFields[settingsField],
                  nodeFields: {
                    ...Object.keys(templateFields[settingsField].nodeFields).filter((k) => { return k !== id }).reduce((nFields: { [index: string]: any }, nfk) => { nFields[nfk] = templateFields[settingsField].nodeFields[nfk]; return nFields }, {})
                  }
                }
              }));
              dispatch(deleteTemplatedComponent(id));
            });
          }
          dispatch(deleteNodeById(id));
        });
        setNodes((nds: Node[]) => {
          return nds.filter((nd) => { return !(closeActions['delNodes'].includes(nd.id))});
        });
        setEdges((eds: Edge<any>[]) => {
          return eds.filter((ed) => { return !(closeActions['delNodes'].includes(ed.source) || closeActions['delNodes'].includes(ed.target))});
        });
      }
      dispatch(setNodeData({ [data.id]: data }));

      // Update usernamePasswordValidation nodes within signIn node submit actions to reflect the username claim and settings automatically.
      if (data['componentType'] === 'signIn') {
        if (data['formSubmitAction']?.id && allFlowcharts[data['formSubmitAction'].id]) {
          allFlowcharts[data['formSubmitAction'].id].components.forEach((cid: string) => {
            if (allComponentsData[cid] && allComponentsData[cid].componentType === 'usernamePasswordValidation') {
              if (
                allComponentsData[cid].usernameType !== data['usernameType'] ||
                allComponentsData[cid].username !== data['username']
              ) {
                dispatch(
                  setNodeData({
                    [cid]: {
                      ...allComponentsData[cid],
                      usernameType: data['usernameType'],
                      username: data['username'],
                    },
                  }),
                );
              }
            }
          });
        }
      }

      if ((data['sessionManagement']) && !isEqual(data['sessionManagement'], allComponentsData[data['id']]['sessionManagement'])) {
        console.log("Update session management");
        const previousSessionOption = allComponentsData[data['id']]['sessionManagement'].option;
        const previousSessionId = allComponentsData[data['id']]['sessionManagement'].sessionId;
        const currentSessionOption = data['sessionManagement'].option;
        const currentSessionId = data['sessionManagement'].sessionId;
        if (previousSessionOption !== currentSessionOption) {
          if (previousSessionOption === 'skipForSpecificSession') {
            if (ssoSession['sessionFlags'][previousSessionId].components.length === 1
              && ssoSession['sessionFlags'][previousSessionId].components.includes(data['id'])) {
              dispatch(deleteJSONSessionFlagById({ id: previousSessionId }));
            } else {
              dispatch(setJSONSessionFlag({ id: previousSessionId, components: ssoSession.sessionFlags[previousSessionId].components.filter((compId: string) => { return compId !== data['id']; })}))
            }
          } else if (previousSessionOption === 'useGlobalSession') {
            const _newSession = cloneDeep(ssoSession);
            dispatch(setJSONSSOSession({
              ..._newSession,
              components: _newSession.components.filter((compId: string) => { return compId !== data['id'] })
            }));
          }
          if (currentSessionOption === 'skipForSpecificSession') {
            if (Object.keys(ssoSession.sessionFlags).includes(currentSessionId)) {
              dispatch(setJSONSessionFlag({
                ...ssoSession.sessionFlags[currentSessionId],
                components: [data['id'], ...ssoSession.sessionFlags[currentSessionId].components]
              }));
            } else {
              dispatch(setJSONSessionFlag({
                id: currentSessionId,
                components: [data['id']]
              }));
            }
          } else if (currentSessionOption === 'useGlobalSession') {
            dispatch(setJSONSSOSession({
              ...ssoSession,
              components: [data['id'], ...ssoSession.components]
            }));
          }
        } else if ((currentSessionOption === 'skipForSpecificSession') && (previousSessionId !== currentSessionId)) {
          if (ssoSession['sessionFlags'][previousSessionId].components.length === 1
            && ssoSession['sessionFlags'][previousSessionId].components.includes(data['id'])) {
            dispatch(deleteJSONSessionFlagById({ id: previousSessionId }));
          } else {
            dispatch(setJSONSessionFlag({ id: previousSessionId, components: ssoSession.sessionFlags[previousSessionId].components.filter((compId: string) => { return compId !== data['id']; })}))
          }
          if (Object.keys(ssoSession.sessionFlags).includes(currentSessionId)) {
            dispatch(setJSONSessionFlag({
              ...ssoSession.sessionFlags[currentSessionId],
              components: [data['id'], ...ssoSession.sessionFlags[currentSessionId].components]
            }));
          } else {
            dispatch(setJSONSessionFlag({
              id: currentSessionId,
              components: [data['id']]
            }));
          }
        }
      }

      // Update sendEmailOTP nodes within custom email verification flows to reflect the email claim selected automatically.
      if (data['componentType'] === 'userInputPage') {
        if (
          data['emailVerificationSendAction']?.id &&
          data['validateEmail'] &&
          allFlowcharts[data['emailVerificationSendAction'].id]
        ) {
          allFlowcharts[data['emailVerificationSendAction'].id].components.forEach((cid: string) => {
            if (allComponentsData[cid] && allComponentsData[cid].componentType === 'sendEmailOTP') {
              let dEmail =
                data['email']['valEmailType'] === 'newEmail'
                  ? data['email']['newEmail']['attributeSource']
                  : data['email']['valPrevEmail']['emailAttribute'];
              if (allComponentsData[cid].emailClaim !== dEmail) {
                dispatch(setNodeData({ [cid]: { ...allComponentsData[cid], emailClaim: dEmail } }));
              }
            }
          });
        }
      }

      if (addSubFlow) {
        let subFlow = subFlowType ? subFlowType : 'formSubmitAction';
        // create form submit action journey
        let label: string = '';
        if (subFlow === 'formSubmitAction') {
          label = 'Submit action ' + (extractComponentLabel(allFlowcharts, data['flowchart'], data['id']) ?? "");
        } else if (subFlow === 'userInfoEndpoint') {
          label = 'UserInfo Endpoint';
        } else if (subFlow === 'emailVerificationSendAction') {
          label = 'Custom Email Verification ' + (extractComponentLabel(allFlowcharts, data['flowchart'], data['id']) ?? "");
        }
        const subFlowFC = init_Flowchart(subFlow, label);
        const temp = subFlowList.concat({ label: label, value: subFlowFC['id'] });
        setSubFlowList(temp);
        dispatch(
          setSubFC({
            ...subFlowFC,
            parentComponentId: data['id'],
            parentFCId: currentFlowchartId,
            subFlowType: subFlow,
          }),
        );
      }
    } else {
      dispatch(setCurrentOpenNodeId(''));
      setShow('');
    }
    if (templatizations) {
      const prevTemplatizations = Object.keys(templatedComponents).includes(data.id) ?
        templatedComponents[data.id].settings.reduce((tpzs: { [index: string]: any }, sField: string) => {
          templateFields[sField].nodeFields[data.id].fieldPaths.forEach((fieldPath: string) => {
            tpzs[fieldPath] = sField;
          });
          return tpzs;
        }, {}) :
        {};
      const _modifiedTemplateFields = cloneDeep(templateFields);
      const _fieldsToUpdate = new Set<string>();
      const _addedFieldsTo = new Set<string>();
      const _removedFieldsFrom = new Set<string>();
      let _tComponent = Object.keys(templatedComponents).includes(data['id']) ? cloneDeep(templatedComponents[data['id']]) : { settings: [], flowchart: Object.keys(allFlowcharts).find((flowchartId: string) => allFlowcharts[flowchartId].components.includes(data['id'])) };
      Object.keys(templatizations).forEach((fieldPath) => {
        if (Object.keys(prevTemplatizations).includes(fieldPath) && prevTemplatizations[fieldPath] !== templatizations[fieldPath]) {
          if (prevTemplatizations[fieldPath]) {
            _fieldsToUpdate.add(prevTemplatizations[fieldPath]);
          }
          if (templatizations[fieldPath]) {
            _fieldsToUpdate.add(templatizations[fieldPath]);
          }
          _removedFieldsFrom.add(prevTemplatizations[fieldPath]);
          _modifiedTemplateFields[prevTemplatizations[fieldPath]].nodeFields[data['id']].fieldPaths = _modifiedTemplateFields[prevTemplatizations[fieldPath]].nodeFields[data['id']].fieldPaths.filter((fp: string) => { return fp !== fieldPath; });
          if (_modifiedTemplateFields[prevTemplatizations[fieldPath]].nodeFields[data['id']].fieldPaths.length === 0) {
            delete _modifiedTemplateFields[prevTemplatizations[fieldPath]].nodeFields[data['id']];
          }
          if (templatizations[fieldPath]) {
            _addedFieldsTo.add(templatizations[fieldPath]);
            if (!Object.keys(_modifiedTemplateFields[templatizations[fieldPath]].nodeFields).includes(data['id'])) {
              _modifiedTemplateFields[templatizations[fieldPath]].nodeFields[data['id']] = { nodeId: data['id'], fieldPaths: [] };
            }
            _modifiedTemplateFields[templatizations[fieldPath]].nodeFields[data['id']].fieldPaths.push(fieldPath);
          }
        } else if (!Object.keys(prevTemplatizations).includes(fieldPath)) {
          _fieldsToUpdate.add(templatizations[fieldPath]);
          _addedFieldsTo.add(templatizations[fieldPath]);
          
          if (!Object.keys(_modifiedTemplateFields[templatizations[fieldPath]].nodeFields).includes(data['id'])) {
            _modifiedTemplateFields[templatizations[fieldPath]].nodeFields[data['id']] = { nodeId: data['id'], fieldPaths: [] };
          }
          _modifiedTemplateFields[templatizations[fieldPath]].nodeFields[data['id']].fieldPaths.push(fieldPath);
        }
      });
      _fieldsToUpdate.forEach((fieldId) => {
        dispatch(setTemplatizationField({
          [fieldId]: cloneDeep(_modifiedTemplateFields[fieldId])
        }));
      });
      _addedFieldsTo.forEach((fieldId) => {
        if (!_tComponent.settings.includes(fieldId)) {
          _tComponent.settings.push(fieldId);
        }
      });
      _removedFieldsFrom.forEach((fieldId) => {
        if (!Object.keys(_modifiedTemplateFields[fieldId]).includes(data['id'])) {
          _tComponent.settings = _tComponent.settings.filter((fid: string) => { return fid !== fieldId; });
        }
      });
      dispatch(setTemplatedComponent({
        [data['id']]: cloneDeep(_tComponent)
      }));
    }
    dispatch(setValidationResults(testValidateJSON(editorState, allFlowcharts)));
  };

  const openSubFlowFC = (data: any, subFlowType: string) => {
    if (data[subFlowType]['id']) {
      setCurrentTabId(data[subFlowType]['id']);
    }
    dispatch(setCurrentOpenNodeId(''));
    setShow('');
  };

  const onCloseContext = (data: any) => {
    switch (data.type) {
      case 'node':
        switch (data.action) {
          case 'edit':
            setShow('popup');
            dispatch(setCurrentOpenNodeId(data.data.id));
            break;
          case 'delete':
            setShow('');
            if (Object.keys(templatedComponents).includes(data.data.id)) {
              templatedComponents[data.data.id].settings.forEach((settingsField: string) => {
                dispatch(setTemplatizationField({
                  [settingsField]: {
                    ...templateFields[settingsField],
                    nodeFields: {
                      ...Object.keys(templateFields[settingsField].nodeFields).filter((k) => { return k !== data.data.id }).reduce((nFields: { [index: string]: any }, nfk) => { nFields[nfk] = templateFields[settingsField].nodeFields[nfk]; return nFields }, {})
                    }
                  }
                }));
                dispatch(deleteTemplatedComponent(data.data.id));
              });
            }
            dispatch(deleteNodeById(data.data.id));
            break;
          default:
            window.alert('This operation is not supported1');
        }
        break;
      case 'edge':
        switch (data.action) {
          case 'delete':
            setShow('');
            dispatch(deleteEdgeById(data.data.id));
            break;
          default:
            window.alert('This operation is not supported1');
        }
        break;
      default:
        window.alert('This operation is not supported2');
        setShow('');
        // setProtocolName('');
        break;
    }
  };
  return {
    show,
    setShow,
    anchorPoint,
    selectedNE,
    onCloseContext,
    onCloseComponent,
    reactFlowWrap,
    onInit,
    nodes,
    edges,
    rightSection,
    setRightSection,
    onNodesChange,
    onEdgesChange,
    onNodeDragStop,
    onConnect,
    onNodeClick,
    onNodeDoubleClick,
    onNodeContextMenu,
    onEdgeContextMenu,
    onPaneContextMenu,
    onEdgeUpdate,
    onEdgeUpdateStart,
    onEdgeUpdateEnd,
    onDragOver,
    onDrop,
    newClaim,
    setNewClaim,
    newAADAttribute,
    setNewAADAttribute,
    currentFlowchartId,
    setCurrentFlowchart,
    setCurrentTabId,
    flowchartsList,
    subFlowList,
    onCloseCreateNewPopup,
    openSubFlowFC,
    currentOpenNodeId,
    currentTabId,
    allComponentsData,
    allFlowcharts,
    deployState,
    setDeployState,
    showFeedbackComp,
    closeFeedback,
    showTOS,
    setShowTOS,
    promptEmail,
    setPromptEmail,
    startWizardCancel,
    deploymentEnv,
    setDeploymentEnv,
    enableJavascript,
    setEnableJavascript,
    journeyInsightsSettings,
    setJourneyInsightsSettings,
    editorState,
    downloadEnv,
    setDownloadEnv,
    templatizedDownload,
    setTemplatizedDownload,
    envsForAppSettings,
    setEnvsForAppSettings,
    addNewFlowchart
  };
};
export default useJSONEditorHook;
